import React, { useState } from 'react';
import { Download, HardDrive, ArrowRight, Check, Play, Square } from 'lucide-react';
import { BUTTON_LABEL, BUTTON_HINT } from '@/components/FootagePullButton';

/**
 * "What's new" — Footage Pull, EDITORS ONLY. There is no admin variant: nothing about
 * the app changes what admin does.
 *
 * TWO STEPS, NOT THREE. There is less to learn than file requests: one picture of where
 * the button is, one picture of what the app does. Each step is a picture with a few
 * numbered rows under it, so the reader looks before they read.
 *
 * ILLUSTRATIONS, NOT SCREENSHOTS — drawn in markup so they follow the theme, reflow on
 * a phone and cannot go stale (the same decision as FileRequestsAnnouncement). The
 * button in step 1 is drawn with the REAL button's classes and its strings imported
 * from FootagePullButton.js, so a reworded button rewords the picture. The app window
 * in step 2 mirrors the Tk window's layout (title, project, destination, Start/Stop,
 * the engine's progress line). The project name and numbers in it are examples.
 *
 * THE HONEST NOTE IS NOT OPTIONAL. WeTransfer provides no checksum, so WeTransfer files
 * are checked by size only, and the app says so when it finishes. The row that says it
 * here mirrors the engine's own finish line (tools/footage_pull.py, "checked by SIZE
 * ONLY") and stays in the announcement by operator decision.
 */

/* ── shared chrome (matches the other announcements) ───────────────────── */

const Eyebrow = ({ children }) => (
  <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 block">
    {children}
  </span>
);

const Numbered = ({ rows }) => (
  <div className="p-6 py-5 flex flex-col gap-2.5 border-t border-ug-border-subtle">
    {rows.map(([t, d], i) => (
      <div key={i} className="flex gap-3">
        <span className="shrink-0 w-5 h-5 rounded-full bg-ug-gold/15 border border-ug-gold/25 text-ug-gold font-sans text-[10px] font-semibold flex items-center justify-center">{i + 1}</span>
        <p className="text-[12px] text-zinc-100/75 leading-relaxed">
          <span className="text-zinc-100 font-medium">{t}.</span> {d}
        </p>
      </div>
    ))}
  </div>
);

/* ── step 1: the button, where it is, and what still goes through the NAS ── */

const FilesTabIllustration = () => (
  <div className="p-3 rounded-xl bg-ug-surface/50 border border-ug-border">
    <Eyebrow>The project’s Files tab</Eyebrow>
    <div className="mt-2 rounded-lg border border-ug-border-strong bg-ug-surface p-3">
      <div className="flex items-center gap-2 flex-wrap">
        {/* The real button's classes and strings. Not clickable here. */}
        <span className="ug-cta tap-auto font-sans text-sm shadow-none pointer-events-none">
          <span className="ug-cta-inner"><Download className="w-4 h-4" /> {BUTTON_LABEL}</span>
        </span>
        <span className="text-[10px] text-slate-500">{BUTTON_HINT}</span>
      </div>
      <p className="mt-2.5 text-[10px] text-slate-500">Dropbox, Google Drive or WeTransfer: the same button, whatever the client sent.</p>
    </div>
    <div className="mt-2.5 flex flex-col gap-1.5">
      {['Uncut Gems NAS', 'MediaShuttle'].map(name => (
        <div key={name} className="flex items-center gap-2 rounded border border-ug-border px-2.5 py-1.5">
          <HardDrive className="w-3 h-3 text-slate-500 shrink-0" />
          <span className="text-[10px] text-slate-400">{name}</span>
          <span className="ml-auto font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500">Still used when sent another way</span>
        </div>
      ))}
    </div>
  </div>
);

/* ── step 2: the app window, mid-download ──────────────────────────────── */

const AppWindowIllustration = () => (
  <div className="p-3 rounded-xl bg-ug-surface/50 border border-ug-border">
    <Eyebrow>Footage Pull, on your computer</Eyebrow>
    {/* The app is dark in both portal themes: it is a separate window with its own look. */}
    <div className="mt-2 rounded-lg overflow-hidden border border-[#262b31] bg-[#0f1114] text-[#f4f4f5]" style={{ colorScheme: 'dark' }}>
      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#171b1f] border-b border-[#262b31]">
        <span className="w-2 h-2 rounded-full bg-[#5c626c]" /><span className="w-2 h-2 rounded-full bg-[#5c626c]" /><span className="w-2 h-2 rounded-full bg-[#5c626c]" />
        {/* The two hardcoded golds here and below are CORRECT and must stay. This announcement is shown to
            editors only (routes/announcements.py, roles=("editor",)), and `site-look` is set for
            role === 'client' and nobody else, so editors keep the portal's own look by design.
            Recolouring these to the site's teal would be the regression, not the fix. KNOWN-ISSUES 0bd. */}
        <span className="ml-2 font-sans text-[9px] font-semibold uppercase tracking-wider text-[#c6a96f]">Footage Pull</span>
      </div>
      <div className="px-3 py-2.5 space-y-1.5">
        {/* Example project and folder: user-visible values, not product copy. */}
        <div className="flex gap-2 text-[10px]"><span className="w-20 shrink-0 text-[#9aa1ad]">Project</span><span className="text-[#f4f4f5]">Harper &amp; Cole — Highlight Film</span></div>
        <div className="flex gap-2 text-[10px]"><span className="w-20 shrink-0 text-[#9aa1ad]">Save to</span><span className="text-[#f4f4f5] truncate">/Volumes/Edit Drive/Harper &amp; Cole</span></div>
        <div className="flex items-center gap-2 pt-1">
          <span className="inline-flex items-center gap-1 rounded-md bg-[#c6a96f] text-[#131719] font-sans text-[9px] font-semibold uppercase tracking-wider px-3 py-1.5"><Play className="w-2.5 h-2.5" /> Start</span>
          <span className="inline-flex items-center gap-1 rounded-md border border-[#262b31] text-[#9aa1ad] font-sans text-[9px] font-semibold uppercase tracking-wider px-3 py-1.5"><Square className="w-2.5 h-2.5" /> Stop</span>
        </div>
        <div className="pt-1.5">
          <div className="h-1.5 rounded-full bg-[#171b1f] overflow-hidden"><div className="h-full rounded-full bg-[#5ba89d]" style={{ width: '62%' }} /></div>
          <p className="mt-1.5 font-mono text-[9px] text-[#9aa1ad]">27 of 41 files · 131 GB of 212 GB · 8 downloading at once</p>
        </div>
        <div className="pt-1 space-y-0.5 font-mono text-[9px] leading-relaxed">
          <p className="text-[#5ba89d] flex items-start gap-1"><Check className="w-2.5 h-2.5 mt-0.5 shrink-0" /> CEREMONY_A7IV_0142.MP4 verified</p>
          <p className="text-[#5ba89d] flex items-start gap-1"><Check className="w-2.5 h-2.5 mt-0.5 shrink-0" /> CEREMONY_A7IV_0143.MP4 verified</p>
          <p className="text-[#9aa1ad]">RECEPTION_FX3_0017.MP4 … 4.1 GB of 6.8 GB</p>
        </div>
      </div>
    </div>
  </div>
);

/* ── the two steps ─────────────────────────────────────────────────────── */

const STEPS = [
  {
    eyebrow: 'Step 1 of 2',
    title: 'Footage comes to you now',
    blurb: 'There is now an app that downloads a project’s footage for you when the client sent it through Dropbox, Google Drive or WeTransfer. For those three you no longer go to the NAS or MediaShuttle. Anything sent another way, a hard drive or a different service, still arrives through the NAS or MediaShuttle as before.',
    body: <FilesTabIllustration />,
    rows: [
      ['Open the project, then the Files tab', `Click ${BUTTON_LABEL}. The app opens with the project already loaded. Nothing to type.`],
      ['First time only', 'You install the app once, and your computer will warn you the first time because the app is new to it. The Editor Guide has the exact steps, for Mac and for Windows.'],
    ],
  },
  {
    eyebrow: 'Step 2 of 2',
    title: 'Press Start, then let it run',
    blurb: 'It downloads many files at once, so it is fast. When it says done, it is done: every file is checked against the source before it counts.',
    body: <AppWindowIllustration />,
    rows: [
      ['Internet dropped, or you closed it', 'Open it again and press Start. It picks up from where it stopped and never re-downloads what it already has.'],
      ['Checked against the source', 'Each file is verified against the provider’s checksum before the app calls it done.'],
      ['One exception, and it tells you', 'WeTransfer provides no checksum, so files from WeTransfer are checked by size only. The app says so when it finishes.'],
    ],
  },
];

/* ── the announcement ──────────────────────────────────────────────────── */

export function FootagePullAnnouncement({ onAcknowledge, embedded = false, forcedStep = null }) {
  const [step, setStep] = useState(0);
  const idx = forcedStep ?? step;
  const last = idx === STEPS.length - 1;
  const s = STEPS[idx];

  const shell = (
    <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-ug-raised border border-ug-border shadow-2xl"
         data-testid="whats-new-footage-pull-editor">
      <div className="p-6 pb-5 border-b border-ug-border-subtle">
        <div className="flex items-center gap-2 mb-3">
          <span className="font-sans text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-ug-gold/15 text-ug-gold border border-ug-gold/25">New</span>
          <Download className="w-3.5 h-3.5 text-ug-gold" strokeWidth={2} />
          <span className="ml-auto font-sans text-[9px] uppercase tracking-wider text-slate-500">{s.eyebrow}</span>
        </div>
        <h2 className="font-heading text-2xl text-zinc-100 mb-2">{s.title}</h2>
        <p className="text-[13px] text-zinc-100/75 leading-relaxed">{s.blurb}</p>
      </div>
      <div className="p-6 py-5 bg-ug-surface/30">{s.body}</div>
      <Numbered rows={s.rows} />
      <div className="p-6 pt-5 border-t border-ug-border-subtle bg-ug-surface/20 flex items-center gap-3">
        <div className="flex gap-1.5">
          {STEPS.map((_, n) => (
            <span key={n} className={`w-1.5 h-1.5 rounded-full ${n === idx ? 'bg-ug-gold' : 'bg-ug-border-strong'}`} />
          ))}
        </div>
        <button
          data-testid="whats-new-next"
          onClick={() => (last ? onAcknowledge && onAcknowledge() : setStep(n => n + 1))}
          className="tap-auto ml-auto px-5 py-2.5 rounded-lg bg-gold-leaf text-obsidian font-sans text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1.5">
          {last ? 'Got it' : 'Next'}
          {!last && <ArrowRight className="w-3 h-3" />}
        </button>
      </div>
    </div>
  );

  if (embedded) return shell;
  return (
    <div className="ug-modal fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
         role="dialog" aria-modal="true" data-testid="whats-new-modal">
      {shell}
    </div>
  );
}

export default FootagePullAnnouncement;
